import { useEffect, useRef, useState } from "react";
import wordpressLogo from "@/assets/integrations/wordpress.svg";
import shopifyLogo from "@/assets/integrations/shopify.svg";
import squarespaceLogo from "@/assets/integrations/squarespace.svg";
import webflowLogo from "@/assets/integrations/webflow.svg";
import googleLogo from "@/assets/integrations/google.svg";

const AVATARS = [
  "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=80&h=80&fit=crop&crop=face",
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face",
];

const PLATFORMS = [
  { name: "WordPress", logo: wordpressLogo },
  { name: "Shopify", logo: shopifyLogo },
  { name: "Squarespace", logo: squarespaceLogo },
  { name: "Webflow", logo: webflowLogo },
  { name: "Google", logo: googleLogo },
];

const useCountUp = (target: number, duration = 2500) => {
  const [value, setValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && !started.current) {
        started.current = true;
        const s = performance.now();
        const tick = (now: number) => {
          const p = Math.min((now - s) / duration, 1);
          setValue(Math.round((1 - Math.pow(1 - p, 3)) * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);
  return { value, ref };
};

const TrustBridge = () => {
  const pages = useCountUp(750000);
  const businesses = useCountUp(2188);
  const marqueeItems = [...PLATFORMS, ...PLATFORMS, ...PLATFORMS];
  return (
    <section style={{ background: "#000000", padding: "32px 20px 48px" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div
          className="trust-bridge-card"
          style={{
            position: "relative",
            borderRadius: 22,
            border: "1px solid rgba(255,255,255,0.07)",
            background:
              "radial-gradient(ellipse at 90% 0%, rgba(0,255,136,0.03) 0%, transparent 55%), linear-gradient(180deg, #050608 0%, #030405 100%)",
            padding: "clamp(24px, 3vw, 36px) clamp(24px, 3vw, 40px)",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          <div className="trust-bridge-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0, 0.85fr) minmax(0, 1.15fr)", gap: "clamp(24px, 4vw, 56px)", alignItems: "center" }}>
            <div>
              <div style={{ color: "#00FF88", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", marginBottom: 12 }}>
                Trusted by businesses worldwide
              </div>
              <h2 style={{ fontSize: "clamp(22px, 2.6vw, 32px)", fontWeight: 800, lineHeight: 1.1, letterSpacing: "-0.02em", color: "#FFFFFF", margin: 0, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                Real results. Real people.<br />Real growth.
              </h2>
              <p style={{ marginTop: 12, fontSize: 14, lineHeight: 1.55, color: "#9CA3AF", maxWidth: 420 }}>
                Join thousands of marketers and business owners who trust OrbitRank to grow their traffic, rankings, and revenue.
              </p>
            </div>

            <div className="trust-bridge-stats" style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "clamp(18px, 2.4vw, 32px)", flexWrap: "nowrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
                <div style={{ display: "flex" }}>
                  {AVATARS.map((src, i) => (
                    <div key={i} style={{ width: 34, height: 34, borderRadius: 999, overflow: "hidden", border: "2px solid #050608", marginLeft: i === 0 ? 0 : -10, background: "#1a1a1a" }}>
                      <img src={src} alt="Customer" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ flexShrink: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, color: "#FBBF24", fontWeight: 800, fontSize: 15, lineHeight: 1 }}>
                  4.9 <span style={{ letterSpacing: 1 }}>★★★★★</span>
                </div>
                <div style={{ fontSize: 10.5, color: "#9CA3AF", marginTop: 4, whiteSpace: "nowrap" }}>5,000+ reviews</div>
              </div>

              <div style={{ width: 1, height: 36, background: "rgba(255,255,255,0.08)", flexShrink: 0 }} />

              <div ref={pages.ref} style={{ flexShrink: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF", lineHeight: 1, whiteSpace: "nowrap" }}>{pages.value.toLocaleString()}</div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "#9CA3AF", textTransform: "uppercase", marginTop: 5, whiteSpace: "nowrap" }}>Pages Generated</div>
              </div>

              <div ref={businesses.ref} style={{ flexShrink: 0 }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: "#FFFFFF", lineHeight: 1, whiteSpace: "nowrap" }}>{businesses.value.toLocaleString()}</div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.14em", color: "#9CA3AF", textTransform: "uppercase", marginTop: 5, whiteSpace: "nowrap" }}>Businesses</div>
              </div>
            </div>
          </div>

          <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />

          <div>
            <div style={{ textAlign: "center", fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em", color: "#9CA3AF", textTransform: "uppercase", marginBottom: 14 }}>
              Works with your website
            </div>
            <div style={{ position: "relative", overflow: "hidden", maskImage: "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)", WebkitMaskImage: "linear-gradient(to right, transparent 0%, #000 8%, #000 92%, transparent 100%)" }}>
              <div className="trust-bridge-marquee" style={{ display: "flex", gap: 56, width: "max-content", animation: "trustMarquee 38s linear infinite" }}>
                {marqueeItems.map((p, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      flexShrink: 0,
                      opacity: 0.55,
                      transition: "opacity 0.3s ease",
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                    onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.55")}
                  >
                    <img src={p.logo} alt={p.name} style={{ width: 22, height: 22, objectFit: "contain" }} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#E5E7EB", whiteSpace: "nowrap" }}>{p.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes trustMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .trust-bridge-marquee:hover { animation-play-state: paused; }
        @media (max-width: 960px) {
          .trust-bridge-grid { grid-template-columns: 1fr !important; }
          .trust-bridge-stats { justify-content: flex-start !important; flex-wrap: wrap !important; }
        }
      `}</style>
    </section>
  );
};

export default TrustBridge;
